import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { RecentlyViewedContext } from '@components/RecentlyViewed/RecentlyViewedProvider'

import Skeleton from '@mui/material/Skeleton'
import PlaceHolder from 'images/placeholder-img.svg'
import _ from 'lodash'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { Link } from 'react-router-dom'
import { SwiperOptions } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { maybe } from '../../utils/misc'
import { ProductPrice } from '@components/Product/ReflectionCard'
import ProductDetailsDialog from '@components/Product/ProductDetailsDialog'
import { getProductUrl } from '../../urls'
import {
  RecentlyViewedProductReflectionsQuery,
  useRecentlyViewedProductReflectionsLazyQuery,
} from '@graphql/queries/__generated__/reflection.generated'
import { useTranslation } from 'react-i18next'
import { useIsVisible } from '../../hooks/useIsVisible'
import { Ordering } from '../../../generated/gqlTypes'

const MAX_SHOW_PRODUCTS = 18

const RecentlyViewedSkeleton: React.FC<{ swiperOptions: SwiperOptions }> = ({ swiperOptions }) => {
  return (
    <Swiper {...swiperOptions}>
      {_.range(6).map((number, idx) => {
        return (
          <SwiperSlide key={idx}>
            <div className='product-thumb text-center'>
              <div className='relative bg-white' style={{ paddingBottom: '138%' }}>
                <LazyLoadImage
                  alt={`New Product Skeleton ${number}`}
                  effect='opacity'
                  src={PlaceHolder}
                  wrapperClassName='w-full'
                  className='absolute inset-0 w-full h-full object-contain object-top'
                />
              </div>
              <div className='h-20 mt-5' />
              <div className='caption'>
                <div className='text-13 line-clamp-3'>
                  <Skeleton width={200} />
                </div>
                <div className='text-13 font-bold'>
                  <Skeleton width={50} />
                </div>
              </div>
            </div>
          </SwiperSlide>
        )
      })}
    </Swiper>
  )
}

const RecentlyViewed: React.FC<{ slidesPerView?: number; align?: 'center' | 'left' | 'right' }> = ({
  slidesPerView,
  align,
}) => {
  const { t } = useTranslation()
  const ref = useRef()
  const isVisible = useIsVisible(ref, { threshold: 0.25 })
  const { value: storageIds } = useContext(RecentlyViewedContext)
  const [activeProductData, setActiveProductData] = useState(null)
  const [loadReflections, reflectionsQueryResult] = useRecentlyViewedProductReflectionsLazyQuery({
    variables: {
      first: 20,
      idIn: storageIds.map(id => `${id}`),
      orderBy: [{ id: Ordering.Desc }],
    },
    // Deliberately bypasses the cache: this query shares the `specifics` field policy with
    // Category's paginated query, and writing its result into the cache was invalidating
    // Category's cached pages, forcing them to refetch from page 1.
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (isVisible && storageIds.length && !reflectionsQueryResult.data) {
      loadReflections()
    }
  }, [isVisible, reflectionsQueryResult.data, storageIds.length])

  const reflections: RecentlyViewedProductReflectionsQuery['specifics']['edges'][0]['node'][] = useMemo(
    () =>
      maybe(
        () =>
          _.sampleSize(
            reflectionsQueryResult.data.specifics.edges.map(edge => edge.node),
            MAX_SHOW_PRODUCTS
          ),
        []
      ),
    [reflectionsQueryResult.data]
  )

  const swiperOptions: SwiperOptions = {
    spaceBetween: 10,
    slidesPerView: slidesPerView || 5,
    lazy: true,
    preloadImages: false,
    pagination: {
      clickable: true,
      el: `#swiper-pagination-recently-viewed`,
    },
    watchSlidesProgress: true,
    loop: false,
    allowSlideNext: reflections.length > 1,
    allowSlidePrev: reflections.length > 1,
    autoplay: {
      disableOnInteraction: true,
      delay: 5000,
      waitForTransition: true,
    },

    // Matches Category's product-grid_5 (Image Size: Medium) column breakpoints.
    breakpoints: {
      0: {
        slidesPerView: 2,
        slidesPerGroup: 2,
        speed: 300,
      },
      600: {
        slidesPerView: 3,
        slidesPerGroup: 3,
        speed: 1000,
      },
      900: {
        slidesPerView: 4,
        slidesPerGroup: 4,
        speed: 1000,
      },
      1280: {
        slidesPerView: slidesPerView || 5,
        slidesPerGroup: slidesPerView || 5,
        speed: 1000,
      },
    },
  }
  const handleClickLink = spec => e => {
    e.preventDefault()
    setActiveProductData({ pk: spec.pk, slug: spec.slug })
  }
  return (
    <section className='my-20' ref={ref}>
      <div className='pt-20 px-12'>
        <h3
          className={`mb-40 font-medium text-4xl md:text-5xl uppercase leading-24 ${
            align ? `text-${align}` : 'text-left'
          }`}
        >
          {t('title.recentlyViewed', 'Recently Viewed')}
        </h3>
      </div>
      <div className='relative px-12 pb-20'>
        {!reflections.length && reflectionsQueryResult.loading && (
          <RecentlyViewedSkeleton swiperOptions={swiperOptions} />
        )}
        <Swiper {...swiperOptions}>
          {reflections.map((node, idx) => {
            return (
              <SwiperSlide key={idx}>
                <div className='product-thumb text-center'>
                  <div className='relative bg-white' style={{ paddingBottom: '138%' }}>
                    <Link
                      to={getProductUrl(node.slug, node.pk)}
                      onClick={handleClickLink(node)}
                      aria-label='product'
                      style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}
                    >
                      <LazyLoadImage
                        alt={node.name}
                        effect='opacity'
                        placeholderSrc={PlaceHolder}
                        src={node.product.thumbnail.lg}
                        className='w-full h-full object-contain object-top'
                      />
                    </Link>
                  </div>
                  <div className='h-20 mt-5' />
                  <div className='caption'>
                    <h4 itemProp='name' className='!mt-0'>
                      <Link
                        aria-label='product'
                        className='animsition-link line-clamp-3'
                        onClick={handleClickLink(node)}
                        to={getProductUrl(node.slug, node.pk)}
                        itemProp='url'
                      >
                        {node.shortName || node.name}
                      </Link>
                    </h4>
                    <ProductPrice priceRange={node.product.priceRange} />
                  </div>
                </div>
              </SwiperSlide>
            )
          })}
        </Swiper>
        <div
          id={`swiper-pagination-recently-viewed`}
          className={`swiper-pagination swiper-pagination-clickable swiper-pagination-bullets w-full ${
            reflections.length > 1 ? 'block' : 'hidden'
          } md:hidden`}
        />
      </div>
      <ProductDetailsDialog
        isOpen={!!activeProductData}
        setIsOpen={setActiveProductData}
        spec={reflections.find(spec => spec.pk === activeProductData?.pk)}
      />
    </section>
  )
}

export default RecentlyViewed
